<template>
  <div class="suggestions-container">
    <div class="title">{{ title }}</div>
    <ol class="suggestions-list">
      <li v-for="(suggestion, idx) in suggestions" :key="idx" class="suggestion">
        <input
          @change="handleSelected(idx)"
          type="checkbox"
          :disabled="isCheckboxDisabled(idx)"
          :checked="isChecked(idx)"
        />{{ idx + 1 }}.
        {{ suggestion }}
      </li>
    </ol>
    <button class="bigmi-continue-button" @click="submitSelections">Copy into study</button>
    <button class="bigmi-cancel-button" @click="refresh">
      {{ startOver }}
    </button>
    <button v-if="isTypeQuestion" class="bigmi-cancel-button" @click="restart">
      Edit your original description and rerun
    </button>
    <div v-if="showModal">
      <Modal>
        <div class="modal-layout">
          <div class="header">
            <div></div>
            <div class="middle">Are you sure?</div>
            <div class="right" @click="closeModal">
              <Fai :icon="['fal', 'times']"/>
            </div>
          </div>
          <div class="content">{{  bodyText }}</div>
          <div v-if="showReplacements">
            <ol class="suggestions-list">
              <li v-for="(rep, idx) in replacing" :key="idx" class="suggestion">
                <input
                  @change="handleReplacementSelected(idx)"
                  type="checkbox"
                  :checked="isReplacementChecked(idx)"
                />{{ idx + 1 }}.
                {{ rep.text }}
              </li>
            </ol>
          </div>
          <div class="button-row">
            <button class="pure-button bigmi-red-2-button each-button" @click="closeModal">
              <span>Cancel</span>
            </button>
            <button class="pure-button bigmi-red-2-button each-button" @click="continueSubmit">
              <span>Replace</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  IDEA_COACH_TYPE,
  SET_IN_PROGRESS_STUDY_QUESTIONS_TEXT,
  GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX,
  SET_IN_PROGRESS_STUDY_ANSWERS_OF_QUESTION_N,
  A_UPDATE_IDEA_COACH_STATE, GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_OBJ, GET_IDEA_COACH_STATE,
} from "@/services/constants";
import { ideaCoachConstructor } from "@/services/utilities";

const titles = {
  question:
    "Select 4 questions from the following to use in the study creation process or use them as a guide to create your own.",
  answer:
    "Select 4 answers from the following to use in the study creation process or use them as a guide to create your own.",
};

export default {
  name: "IdeaCoachSelection",
  data() {
    return {
      selectedSuggestions: [],
      selectedReplacements: [],
      showModal: false
    };
  },
  props: {
    type: {
      type: String,
      default: "",
    },
    suggestions: {
      type: Array,
      default: () => [],
    }
  },
  computed: {
    ...mapGetters([GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX, GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_OBJ, GET_IDEA_COACH_STATE]),
    title() {
      return this.isTypeQuestion ? titles.question : titles.answer;
    },
    startOver() {
      return this.isTypeQuestion
        ? "Rerun for another 15 questions"
        : "Rerun for another 15 answers";
    },
    isTypeQuestion() {
      return this.type === IDEA_COACH_TYPE.question;
    },
    questions() {
      return this.$store.state.dataModule.inProgressStudy.questions.filter(s => !!s.text)
    },
    answers() {
      return this[GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_OBJ].answers.filter(s => !!s.text)
    },
    replacing() {
      return this.isTypeQuestion ? this.questions : this.answers
    },
    bodyText() {
      const type = this.isTypeQuestion ? "questions" : "answers"
      if (this.selectedSuggestions.length < 4) {
        return `Please identify which ${type} you would like to replace. Choose ${this.needToFill}.`
      }
      else {
        return `Are you sure you want to replace ${type}?`
      }
    },
    showReplacements() {
      return this.selectedSuggestions.length < 4
    },
    needToFill() {
      return this.selectedSuggestions.length + this.replacing.length - 4;
    }
  },
  methods: {
    toggleValueInSelected(idx) {
      if (this.selectedSuggestions.includes(idx)) {
        const selectedIndex = this.selectedSuggestions.findIndex((index) => index === idx);
        this.selectedSuggestions.splice(selectedIndex, 1);
      } else {
        this.selectedSuggestions.push(idx);
      }
    },
    handleSelected(idx) {
      this.toggleValueInSelected(idx);
    },
    isCheckboxDisabled(idx) {
      return this.selectedSuggestions.length >= 4 && !this.selectedSuggestions.includes(idx);
    },
    handleReplacementSelected(idx) {
      if (this.selectedReplacements.includes(idx)) {
        const selectedIndex = this.selectedReplacements.findIndex((index) => index === idx);
        this.selectedReplacements.splice(selectedIndex, 1);
      } else {
        this.selectedReplacements.push(idx);
      }
    },
    isReplacementChecked(idx) {
      return this.selectedReplacements.includes(idx);
    },
    submitSelections() {
      if (this.isTypeQuestion) {
        this.submitQuestions()
      } else {
        this.submitAnswers()
      }
    },
    submitAnswers() {
      if (this.answers.length === 0) {
        const selected = this.selectedSuggestions.map((id) => {
          return {
            text: this.suggestions[id],
          };
        });
        this.$store.commit(SET_IN_PROGRESS_STUDY_ANSWERS_OF_QUESTION_N, {
          i: this[GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX],
          answers: selected,
        });
        this.$emit("submit");
      }
      else if (this.selectedSuggestions.length + this.answers.length <= 4) {
        const selected = this.selectedSuggestions.map((id) => {
          return {
            text: this.suggestions[id],
          };
        });
        const all = this.answers.concat(selected)
        this.$store.commit(SET_IN_PROGRESS_STUDY_ANSWERS_OF_QUESTION_N, {
          i: this[GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX],
          answers: all,
        });
        this.$emit("submit");
      }
      else {
        this.showModal = true
      }
    },
    submitQuestions() {
      if (this.questions.length === 0) {
        const selected = this.selectedSuggestions.map((id) => {
          return {
            text: this.suggestions[id],
          };
        });
        this.$store.commit(SET_IN_PROGRESS_STUDY_QUESTIONS_TEXT, selected);
        this.$emit("submit");
      }
      else if (this.selectedSuggestions.length + this.questions.length <= 4) {
        const selected = this.selectedSuggestions.map((id) => {
          return {
            text: this.suggestions[id],
          };
        });
        const all = this.questions.concat(selected)
        this.$store.commit(SET_IN_PROGRESS_STUDY_QUESTIONS_TEXT, all);
        this.$emit("submit");
      }
      else {
        this.showModal = true
      }
    },
    continueSubmit() {
      if (this.isTypeQuestion) {
        this.continueSubmitQuestions()
      } else {
        this.continueSubmitAnswers()
      }
    },
    continueSubmitQuestions() {
      const selected = this.selectedSuggestions.map((id) => {
        return {
          text: this.suggestions[id],
        };
      });
      if (this.selectedSuggestions.length === 4) {
        this.$store.commit(SET_IN_PROGRESS_STUDY_QUESTIONS_TEXT, selected);
        this.$emit("submit");
      }
      else if (this.selectedReplacements.length === this.needToFill) {
        const all = []
        for (let i = 0; i < this.questions.length; i++) {
          if (!this.selectedReplacements.includes(i))
            all.push(this.questions[i])
        }
        this.$store.commit(SET_IN_PROGRESS_STUDY_QUESTIONS_TEXT, all.concat(selected));
        this.$emit("submit");
      }
    },
    continueSubmitAnswers() {
      const selected = this.selectedSuggestions.map((id) => {
        return {
          text: this.suggestions[id],
        };
      });
      if (this.selectedSuggestions.length === 4) {
        this.$store.commit(SET_IN_PROGRESS_STUDY_ANSWERS_OF_QUESTION_N, {
          i: this[GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX],
          answers: selected,
        });
        this.$emit("submit");
      }
      else if (this.selectedReplacements.length === this.needToFill) {
        const all = []
        for (let i = 0; i < this.answers.length; i++) {
          if (!this.selectedReplacements.includes(i))
            all.push(this.answers[i])
        }
        this.$store.commit(SET_IN_PROGRESS_STUDY_ANSWERS_OF_QUESTION_N, {
          i: this[GET_IN_PROGRESS_STUDY_CURRENT_QUESTION_INDEX],
          answers: all.concat(selected),
        });
        this.$emit("submit");
      }
    },
    isChecked(idx) {
      return this.selectedSuggestions.includes(idx);
    },
    refresh() {
      this.$emit("reset", this[GET_IDEA_COACH_STATE].description);
    },
    restart() {
      this.$store.dispatch(A_UPDATE_IDEA_COACH_STATE, { currentStep: 1 });
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../scss/main";
.suggestions-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title {
  position: sticky;
  top: 0;
  text-align: center;
  font-weight: 600;
  font-size: 1.25rem;
  margin-top: 2rem;
}

.suggestions-list {
  height: 75%;
  overflow: auto;
}

.suggestion {
  display: flex;
  align-items: flex-start;
  margin: 1rem 0;
}

.suggestion > * {
  margin: 0 0.5rem 0 0;
}

button {
  width: 66%;
}

button:not(:first-child) {
  margin: 0.5rem 0 0 0;
}

.modal-layout {
  background-color: #ffffff;
  padding: 30px;
  border-radius: 3px;

  .header {
    display: grid;
    grid-template-columns: 15% 70% 15%;
    padding: 10px 10px;

    .left {

    }

    .middle {
      justify-self: center;
      color: $red-2;
      font-size: 20px;
      font-weight: 900;
    }

    .right {
      justify-self: right;
      font-size: 20px;
    }

    .right:hover {
      cursor: pointer;
    }
  }

  .content {
    padding: 20px 10px;
    line-height: 1.2;
  }

  .button-row {
    display: flex;
    justify-content: space-around;
    padding-top: 20px;

    .each-button {
      flex-basis: 33%;
    }

    button {
      margin: 0 0 0 0;
    }
  }
}
</style>
